import type { BeforeSendFn, Properties } from 'posthog-js';

import { REDACTED_JWT } from '@/logger/redaction/redaction.const';

const JWT_HEADER = 'eyJ';
const DOTTED_RUN_PATTERN = /[\w.-]+/g;

function findJwtStart(header: string, payload: string | undefined, signature: string | undefined): number {
  const start = header.indexOf(JWT_HEADER);
  const hasHeader = start !== -1 && header.length > start + JWT_HEADER.length;
  const hasPayload = payload !== undefined && payload.startsWith(JWT_HEADER) && payload.length > JWT_HEADER.length;

  return hasHeader && hasPayload && Boolean(signature) ? start : -1;
}

function redactRun(run: string): string {
  const segments = run.split('.');
  const redacted: string[] = [];

  for (let index = 0; index < segments.length; index++) {
    const start = findJwtStart(segments[index], segments[index + 1], segments[index + 2]);

    if (start === -1) {
      redacted.push(segments[index]);
    } else {
      redacted.push(`${segments[index].slice(0, start)}${REDACTED_JWT}`);
      index += 2;
    }
  }

  return redacted.join('.');
}

function redactString(value: string): string {
  return value.includes(JWT_HEADER) ? value.replace(DOTTED_RUN_PATTERN, redactRun) : value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function redactJwts(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactString(value);
  }

  if (Array.isArray(value)) {
    return value.map(redactJwts);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [redactString(key), redactJwts(item)]),
    );
  }

  return value;
}

function redactProperties(properties: Properties): Properties {
  return redactJwts(properties) as Properties;
}

export const redactCredentials: BeforeSendFn = (captureResult) => {
  if (!captureResult) {
    return captureResult;
  }

  const { $set, $set_once } = captureResult;

  return {
    ...captureResult,
    properties: redactProperties(captureResult.properties),
    ...($set && { $set: redactProperties($set) }),
    ...($set_once && { $set_once: redactProperties($set_once) }),
  };
};
